import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../config/api';
import { getLiveSocket } from '../../utils/liveSocket';

const POLL_MS = 60000; // fallback only; Socket.IO delivers changes instantly

/**
 * Global sticky live player. Mounted ONCE in App.js, above the dashboards.
 * - Owns the live-status polling + Socket.IO listeners for the signed-in
 *   student/parent (the server derives the class from the user id).
 * - Collapsed: full-width red banner. Expanded: 16:9 YouTube embed slides open
 *   below the banner and pushes page content down (normal flow, not an overlay).
 * - Renders nothing when not live or for roles without a class stream.
 */
export default function GlobalLivePlayer({ user, topOffset = 0, onHeightChange }) {
  const role = user?.role;
  const eligible = role === 'student' || role === 'parent';
  const userId = String(user?._id || user?.id || '');

  const [stream, setStream] = useState(null); // { title, embedUrl } while live
  const [expanded, setExpanded] = useState(false);
  const [muted, setMuted] = useState(true);
  const [frameKey, setFrameKey] = useState(0);
  const myClassId = useRef(null);
  const wrapperRef = useRef(null);
  const active = eligible && !!stream;

  const checkStatus = useCallback(async (signal) => {
    if (!eligible || !userId) return;
    try {
      const res = await axios.get(`${API_BASE_URL}/api/livestream/my-stream`, {
        headers: { 'x-user-id': userId },
        signal,
        timeout: 15000
      });
      if (res.data?.classId) myClassId.current = String(res.data.classId);
      if (res.data?.isLive && res.data?.embedUrl) {
        setStream((prev) =>
          prev && prev.embedUrl === res.data.embedUrl
            ? prev
            : { title: res.data.title || '', embedUrl: res.data.embedUrl }
        );
      } else {
        setStream(null);
      }
    } catch {
      /* keep last known status on transient errors */
    }
  }, [eligible, userId]);

  useEffect(() => {
    if (!eligible || !userId) {
      setStream(null);
      return undefined;
    }
    const controller = new AbortController();
    checkStatus(controller.signal);
    const timer = setInterval(() => checkStatus(controller.signal), POLL_MS);

    const socket = getLiveSocket();
    const onChange = (payload) => {
      if (!myClassId.current || String(payload?.classId) === myClassId.current) checkStatus();
    };
    socket.on('liveStreamStarted', onChange);
    socket.on('liveStreamStopped', onChange);

    return () => {
      controller.abort();
      clearInterval(timer);
      socket.off('liveStreamStarted', onChange);
      socket.off('liveStreamStopped', onChange);
    };
  }, [eligible, userId, checkStatus]);

  // <LiveStatusBadge/> in the header asks us to open the player.
  useEffect(() => {
    if (!active) return undefined;
    const open = () => {
      setExpanded(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    };
    window.addEventListener('live-status:open-player', open);
    return () => window.removeEventListener('live-status:open-player', open);
  }, [active]);

  // When the class ends, reset the player for the next session.
  useEffect(() => {
    if (!stream) {
      setExpanded(false);
      setMuted(true);
    }
  }, [stream]);

  // Report the live rendered height (banner + animated video panel) to App so it
  // can pad the page by exactly that much. Reports 0 whenever the player is hidden.
  useEffect(() => {
    if (!active || !wrapperRef.current) {
      if (onHeightChange) onHeightChange(0);
      return undefined;
    }
    const el = wrapperRef.current;
    const report = () => onHeightChange && onHeightChange(Math.round(el.getBoundingClientRect().height));
    report();
    const ro = new ResizeObserver(report);
    ro.observe(el);
    return () => {
      ro.disconnect();
      if (onHeightChange) onHeightChange(0);
    };
  }, [active, onHeightChange]);

  if (!active) return null;

  const embedSrc = muted ? stream.embedUrl : stream.embedUrl.replace('mute=1', 'mute=0');

  const handleUnmute = () => {
    // The tap is a user gesture, so the browser permits audio after the reload.
    setMuted(false);
    setFrameKey((k) => k + 1);
  };

  const collapse = () => {
    setExpanded(false);
    setMuted(true);
  };

  return (
    <div
      ref={wrapperRef}
      className="fixed left-0 top-0 z-[60] w-full max-w-full overflow-hidden bg-white shadow-md"
      style={{ top: topOffset }}
    >
      {/* Banner (always visible while live) */}
      <div className="flex w-full items-stretch bg-red-600 text-white">
        <button
          type="button"
          onClick={() => (expanded ? collapse() : setExpanded(true))}
          aria-expanded={expanded}
          aria-controls="global-live-player-panel"
          className="flex min-h-[48px] flex-1 items-center justify-center gap-2 px-3 py-3 text-center text-sm font-bold hover:bg-red-700 active:bg-red-800 sm:text-base"
        >
          <span className="relative flex h-3 w-3 shrink-0" aria-hidden="true">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white opacity-75" />
            <span className="relative inline-flex h-3 w-3 rounded-full bg-white" />
          </span>
          <span>🔴 LIVE: Your class is running! {expanded ? 'Tap to minimize.' : 'Tap to watch.'}</span>
        </button>
        {expanded && (
          <button
            type="button"
            onClick={collapse}
            aria-label="Close live video"
            className="flex w-12 shrink-0 items-center justify-center text-2xl font-bold hover:bg-red-700 active:bg-red-800"
          >
            ✕
          </button>
        )}
      </div>

      {/* Smooth expand/collapse: animate grid row 0fr -> 1fr */}
      <div
        id="global-live-player-panel"
        style={{
          display: 'grid',
          gridTemplateRows: expanded ? '1fr' : '0fr',
          transition: 'grid-template-rows 300ms ease-in-out'
        }}
      >
        <div style={{ minHeight: 0, overflow: 'hidden' }}>
          {expanded && (
            <div className="mx-auto w-full max-w-3xl bg-black">
              <div className="relative w-full" style={{ aspectRatio: '16 / 9' }}>
                <iframe
                  key={frameKey}
                  src={embedSrc}
                  title={stream.title || 'Live Classroom'}
                  className="absolute inset-0 h-full w-full border-0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                  allowFullScreen
                  referrerPolicy="strict-origin-when-cross-origin"
                />
                {muted && (
                  <button
                    type="button"
                    onClick={handleUnmute}
                    className="absolute bottom-3 left-1/2 -translate-x-1/2 rounded-full bg-blue-600 px-5 py-3 text-base font-bold text-white shadow-lg active:scale-95"
                  >
                    🔊 Tap to Unmute
                  </button>
                )}
              </div>
              {stream.title && (
                <p className="truncate bg-red-700 px-3 text-white py-2 text-center text-sm font-semibold">{stream.title}</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

